import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import FadeIn from "@/components/FadeIn";
import SkylineDivider from "@/components/SkylineDivider";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

function isoDate(d) {
  return d.toISOString().slice(0, 10);
}

function todayISO() {
  return isoDate(new Date());
}

function nightsBetween(ci, co) {
  if (!ci || !co) return 0;
  const a = new Date(ci);
  const b = new Date(co);
  return Math.max(0, Math.round((b - a) / 86400000));
}

export default function Book() {
  const { slug } = useParams();
  const nav = useNavigate();

  const [cottages, setCottages] = useState([]);
  const [cottageSlug, setCottageSlug] = useState(slug || "");
  const [checkIn, setCheckIn] = useState("");
  const [checkOut, setCheckOut] = useState("");
  const [guestName, setGuestName] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [guestPhone, setGuestPhone] = useState("");
  const [partySize, setPartySize] = useState(1);
  const [message, setMessage] = useState("");
  const [quote, setQuote] = useState(null);
  const [bookedDates, setBookedDates] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [conflict, setConflict] = useState("");
  const [agreedTerms, setAgreedTerms] = useState(false);

  const cottage = useMemo(() => cottages.find((c) => c.slug === cottageSlug), [cottages, cottageSlug]);
  const inquiryOnly = !!cottage?.inquiry_only;

  useEffect(() => {
    fetch(`${API}/api/cottages`)
      .then((r) => r.json())
      .then((d) => {
        setCottages(d.cottages || []);
        if (!cottageSlug && d.cottages?.length) setCottageSlug(d.cottages[0].slug);
      });
  }, []); // eslint-disable-line

  // Refresh availability whenever cottage changes (windowed to next 6 months)
  useEffect(() => {
    if (!cottageSlug) return;
    const start = todayISO();
    const end = isoDate(new Date(Date.now() + 180 * 86400000));
    fetch(`${API}/api/cottages/${cottageSlug}/availability?start=${start}&end=${end}`)
      .then((r) => r.json())
      .then((d) => setBookedDates(d.booked_dates || []))
      .catch(() => setBookedDates([]));
  }, [cottageSlug]);

  // Live quote refresh
  useEffect(() => {
    setConflict("");
    if (!cottageSlug || !checkIn || !checkOut) return setQuote(null);
    if (nightsBetween(checkIn, checkOut) < 1) return setQuote(null);
    const ctrl = new AbortController();
    fetch(`${API}/api/cottages/${cottageSlug}/quote`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ check_in: checkIn, check_out: checkOut }),
      signal: ctrl.signal,
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setQuote(d))
      .catch(() => {});
    // conflict pre-check against local booked set
    const s = new Date(checkIn);
    const e = new Date(checkOut);
    for (let d = new Date(s); d < e; d.setDate(d.getDate() + 1)) {
      if (bookedDates.includes(isoDate(d))) {
        setConflict("These dates overlap with existing bookings — please pick different dates.");
        break;
      }
    }
    return () => ctrl.abort();
  }, [cottageSlug, checkIn, checkOut, bookedDates]);

  async function submit(e) {
    e.preventDefault();
    if (submitting) return;
    if (!agreedTerms) {
      toast.error("Please agree to the booking terms and conditions to continue.");
      return;
    }
    setSubmitting(true);
    try {
      // For inquiry-only cottages, default missing dates to today + 1 day so backend
      // accepts the request (owner will replace with real dates after outreach).
      const submitCheckIn = checkIn || (inquiryOnly ? todayISO() : "");
      const submitCheckOut = checkOut || (inquiryOnly ? isoDate(new Date(Date.now() + 86400000)) : "");
      const res = await fetch(`${API}/api/bookings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          cottage_slug: cottageSlug,
          check_in: submitCheckIn,
          check_out: submitCheckOut,
          guest_name: guestName,
          guest_email: guestEmail,
          guest_phone: guestPhone || null,
          party_size: Number(partySize),
          message: message || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || "Could not create request. Please try again.");
        return;
      }
      toast.success("Request received — check your email for confirmation.");
      nav(`/book/confirmed/${data.reservation_id}`);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-cream" data-testid="book-page">
      <section className="pt-32 pb-8 lg:pt-40 lg:pb-12">
        <div className="container-narrow">
          <p className="overline mb-4">Reserve or Inquire</p>
          <h1 className="font-serif text-4xl lg:text-6xl text-moss font-light leading-[1.05]">
            Request a stay.
          </h1>
          <p className="mt-6 max-w-2xl text-coal/70 leading-[1.85] text-lg">
            Pick your cottage and dates, or use the notes field below to ask us anything.
            We'll hold your dates on our calendar, send a confirmation email, and reach out
            personally to process payment through our Clover terminal — no online payment required up front.
          </p>
        </div>
      </section>
      <SkylineDivider />

      <section className="py-16 lg:py-24">
        <div className="container-narrow">
          <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12" data-testid="book-form">
            {/* Left — inputs */}
            <div className="lg:col-span-3 space-y-6">
              <Field label="Cottage" testid="field-cottage">
                <select
                  data-testid="input-cottage"
                  value={cottageSlug}
                  onChange={(e) => setCottageSlug(e.target.value)}
                  className="w-full bg-white border border-moss/25 px-4 py-3 text-coal focus:outline-none focus:border-moss"
                >
                  {cottages.map((c) => (
                    <option key={c.slug} value={c.slug} data-testid={`option-${c.slug}`}>
                      {c.inquiry_only
                        ? c.name
                        : `${c.name} — from $${c.base_weekday || c.base_nightly}/night · sleeps ${c.sleeps}`}
                    </option>
                  ))}
                </select>
                {inquiryOnly && (
                  <p className="mt-2 text-xs text-clay italic" data-testid="inquiry-only-note">
                    We'll coordinate dates and pricing with you personally after you send this — no need to pin down specific dates now.
                  </p>
                )}
              </Field>

              <div className="grid grid-cols-2 gap-4">
                <Field label={inquiryOnly ? "Preferred arrival (approx.)" : "Check-in"} testid="field-check-in">
                  <input
                    data-testid="input-check-in"
                    type="date"
                    required={!inquiryOnly}
                    min={todayISO()}
                    value={checkIn}
                    onChange={(e) => setCheckIn(e.target.value)}
                    className="w-full bg-white border border-moss/25 px-4 py-3 text-coal focus:outline-none focus:border-moss"
                  />
                </Field>
                <Field label={inquiryOnly ? "Preferred departure (approx.)" : "Check-out"} testid="field-check-out">
                  <input
                    data-testid="input-check-out"
                    type="date"
                    required={!inquiryOnly}
                    min={checkIn || todayISO()}
                    value={checkOut}
                    onChange={(e) => setCheckOut(e.target.value)}
                    className="w-full bg-white border border-moss/25 px-4 py-3 text-coal focus:outline-none focus:border-moss"
                  />
                </Field>
              </div>

              {!inquiryOnly && (
                <p className="text-xs text-coal/55 italic -mt-2" data-testid="min-stay-note">
                  Two-night minimum. Weekend rate applies Fri &amp; Sat nights.
                </p>
              )}

              {!inquiryOnly && checkIn && checkOut && nightsBetween(checkIn, checkOut) === 1 && (
                <div data-testid="min-stay-warning" className="text-clay text-sm border-l-2 border-clay/60 pl-4 italic">
                  Two-night minimum — please choose at least 2 nights.
                </div>
              )}

              {conflict && !inquiryOnly && (
                <div data-testid="conflict-warning" className="text-clay text-sm border-l-2 border-clay/60 pl-4 italic">
                  {conflict}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <Field label="Party size" testid="field-party-size">
                  <input
                    data-testid="input-party-size"
                    type="number"
                    min="1"
                    max={cottage?.sleeps || 4}
                    required
                    value={partySize}
                    onChange={(e) => setPartySize(e.target.value)}
                    className="w-full bg-white border border-moss/25 px-4 py-3 text-coal focus:outline-none focus:border-moss"
                  />
                </Field>
                <Field label="Phone (optional)" testid="field-phone">
                  <input
                    data-testid="input-phone"
                    type="tel"
                    value={guestPhone}
                    onChange={(e) => setGuestPhone(e.target.value)}
                    className="w-full bg-white border border-moss/25 px-4 py-3 text-coal focus:outline-none focus:border-moss"
                  />
                </Field>
              </div>

              <Field label="Your name" testid="field-name">
                <input
                  data-testid="input-name"
                  type="text"
                  required
                  minLength={2}
                  value={guestName}
                  onChange={(e) => setGuestName(e.target.value)}
                  className="w-full bg-white border border-moss/25 px-4 py-3 text-coal focus:outline-none focus:border-moss"
                />
              </Field>

              <Field label="Email" testid="field-email">
                <input
                  data-testid="input-email"
                  type="email"
                  required
                  value={guestEmail}
                  onChange={(e) => setGuestEmail(e.target.value)}
                  className="w-full bg-white border border-moss/25 px-4 py-3 text-coal focus:outline-none focus:border-moss"
                />
              </Field>

              <Field label="Anything we should know? (optional)" testid="field-message">
                <textarea
                  data-testid="input-message"
                  rows={4}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="w-full bg-white border border-moss/25 px-4 py-3 text-coal focus:outline-none focus:border-moss resize-none"
                  placeholder="Celebrating something? Traveling with a service animal? Getting off the AT? Let us know."
                />
              </Field>
            </div>

            {/* Right — summary */}
            <div className="lg:col-span-2">
              <div className="sticky top-32 bg-oat p-8 lg:p-10 border border-moss/10" data-testid="book-summary">
                <p className="overline mb-4">Your request</p>
                <h3 className="font-serif text-2xl text-moss font-medium">
                  {cottage?.name || "Pick a cottage"}
                </h3>
                {inquiryOnly ? (
                  <div className="mt-6 text-sm text-coal/80 space-y-3">
                    <p>You're requesting a group stay. Once we get your note we'll reach out with availability and pricing tailored to your group.</p>
                    <p className="pt-3 text-xs text-coal/55 italic border-t border-moss/10">
                      Approx. dates are optional but help us hold time on the calendar.
                    </p>
                  </div>
                ) : quote ? (
                  <div className="mt-6 space-y-3 text-sm text-coal/80">
                    <Row label="Nights" value={quote.nights} testid="summary-nights" />
                    <Row label="Weekday base (Sun–Thu)" value={`$${(quote.base_weekday ?? quote.base_nightly).toFixed(2)}`} />
                    <Row label="Weekend base (Fri–Sat)" value={`$${(quote.base_weekend ?? quote.base_nightly).toFixed(2)}`} />
                    <Row label="Estimated total" value={`$${quote.total.toFixed(2)}`} bold testid="summary-total" />
                    <p className="pt-3 text-xs text-coal/55 italic border-t border-moss/10">
                      Fri &amp; Sat nights are weekend rate.
                      Final total will be confirmed with you before payment.
                    </p>
                  </div>
                ) : (
                  <p className="mt-6 text-sm text-coal/55 italic">
                    Pick dates to see your estimated total.
                  </p>
                )}
                <div className="mt-6 pt-6 border-t border-moss/10">
                  <label className="flex items-start gap-3 cursor-pointer group" data-testid="terms-agree-wrap">
                    <input
                      type="checkbox"
                      checked={agreedTerms}
                      onChange={(e) => setAgreedTerms(e.target.checked)}
                      className="mt-1 h-4 w-4 shrink-0 accent-moss cursor-pointer"
                      data-testid="terms-agree-checkbox"
                    />
                    <span className="text-xs text-coal/75 leading-relaxed">
                      I agree to the{" "}
                      <a
                        href="/terms"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-moss underline underline-offset-2 hover:text-clay"
                        data-testid="terms-link"
                      >
                        booking terms &amp; conditions
                      </a>
                      {" "}(opens in a new tab). An invoice will be sent to you once we approve your
                      stay, and these terms apply directly to that booking.
                    </span>
                  </label>
                </div>
                <button
                  data-testid="submit-request"
                  type="submit"
                  disabled={submitting || !agreedTerms || (!inquiryOnly && (!!conflict || !quote || (checkIn && checkOut && nightsBetween(checkIn, checkOut) < 2)))}
                  className="mt-6 w-full bg-moss text-cream py-4 text-sm tracking-[0.28em] uppercase hover:bg-sageDeep transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {submitting ? "Sending…" : inquiryOnly ? "Send inquiry" : "Request stay · Pay later"}
                </button>
                <p className="mt-4 text-[11px] text-coal/55 leading-relaxed">
                  Your dates will be held on our internal calendar. We'll email you a confirmation
                  right away and reach out personally to process payment through our Clover terminal.
                  No card required now.
                </p>
              </div>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}

function Field({ label, children, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <span className="block text-[11px] uppercase tracking-[0.28em] text-moss mb-2">{label}</span>
      {children}
    </label>
  );
}

function Row({ label, value, bold, testid }) {
  return (
    <div className="flex items-baseline justify-between" data-testid={testid}>
      <span className="text-coal/60">{label}</span>
      <span className={bold ? "text-moss font-serif text-2xl" : "text-coal"}>{value}</span>
    </div>
  );
}
